import { saveQuickConnectIntent } from "features/onboarding/guided/quickConnectIntent";
import Button from "common/Buttons/Button";
import styled, { useTheme } from "styled-components";
import NewLookQuickConnect from "features/portfolio/CreateAccount/NewLook/NewLookQuickConnect";
import { useContext } from "react";
import { SecondaryButton } from "features/configuration/common/styles";
import useMedia from "utils/hooks/useMedia";
import {
  MOBILE_SEARCH,
  QUICK_CONNECT_DATA_QUERY_PARAM,
} from "common/constants";
import { OnboardingStep } from "analytics/events";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import useQuickConnectForm from "@shared/features/onboarding/hooks/useQuickConnectForm";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import WhitelistIPsMessage from "features/configuration/accounts/CreateAccount/WhitelistIPsMessage";
import { useUser } from "@shared/utils/hooks/misc";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import { OpenConfirmModal } from "@shared/types";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { useTelegramWebRedirect } from "utils/hooks/useTelegramWebApp.ts";
import { QUICK_CONNECT_SUBMIT_BUTTON_ID } from "testIds.ts";
import { displayResultMessage } from "utils/misc.tsx";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import {
  getConnectContext,
  useAccountSetupAnalytics,
} from "analytics/accountSetup";

interface QuickConnectFormProps {
  primaryExchange?: string;
  openConfirmModal: OpenConfirmModal;
  /** How it works is explained beside the form (wide onboarding). */
  explainedBeside?: boolean;
}

const QuickConnectForm = ({
  primaryExchange,
  openConfirmModal,
  explainedBeside,
}: QuickConnectFormProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const isOnboarding = !!useContext(OnboardingContext);
  const trackAccountSetup = useAccountSetupAnalytics();
  const connectProperties = {
    context: getConnectContext(isOnboarding),
    exchange: primaryExchange,
    connect_method: "quick_connect" as const,
  };
  const { closeDrawer, startAccountsPolling } =
    useContext(QuickConnectContext) || {};
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { user } = useUser();
  const { redirectToWebsite, isLoading: isLoadingRedirect } =
    useTelegramWebRedirect();
  const { isNewLook } = useTheme();

  const {
    quotaModal,
    noFuturesAccountMessageData,
    quickConnectAlreadyUsedMessageData,
    primaryButtonData,
    secondaryButtonData,
  } = useQuickConnectForm({
    primaryExchange,
    isOnboarding,
    trackAnalyticsConnectClicked: () => {
      if (primaryExchange) {
        analytics?.onboardingConnectToExchangeClicked({
          onboardingStep: OnboardingStep.CONNECT_EXCHANGE,
          clientExchange: primaryExchange,
          createClientIsQuickConnect: true,
        });
      }
    },
    onConnectClicked: () =>
      trackAccountSetup("Connect Account - Connect Clicked", connectProperties),
    openAuthURL: (url) => {
      saveQuickConnectIntent(user?.id, url);
      window.open(url, "_self");
    },
    useQuotaModal,
    openConfirmModal,
    redirectToWebQuickConnect: IS_TELEGRAM_WEB_APP
      ? (args) => {
          try {
            const parsedQuickConnectData = btoa(JSON.stringify(args));
            // The Mini App hands quick connect to the website; when that
            // can't start, the user is left on the form.
            const trackHandOffFailed = (reason: string) =>
              trackAccountSetup("Connect Account - Hand-off Failed", {
                ...connectProperties,
                reason,
              });
            redirectToWebsite({
              [QUICK_CONNECT_DATA_QUERY_PARAM]: parsedQuickConnectData,
            })
              .then((opened) => {
                if (!opened) trackHandOffFailed("link_not_opened");
                startAccountsPolling?.();
                closeDrawer?.();
              })
              .catch(() => trackHandOffFailed("init_data_request_failed"));
          } catch {
            trackAccountSetup("Connect Account - Hand-off Failed", {
              ...connectProperties,
              reason: "encoding_failed",
            });
            return;
          }
        }
      : undefined,
    displayResultMessage,
  });

  if (isNewLook) {
    return (
      <NewLookQuickConnect
        primaryExchange={primaryExchange}
        explainedBeside={explainedBeside}
        messages={
          <>
            {noFuturesAccountMessageData?.displayMessage}
            {quickConnectAlreadyUsedMessageData?.displayMessage}
          </>
        }
        buttonText={primaryButtonData.text}
        onClick={primaryButtonData.onClick}
        loading={primaryButtonData.loading || isLoadingRedirect}
        disabled={primaryButtonData.disabled}
      >
        {quotaModal}
      </NewLookQuickConnect>
    );
  }

  return (
    <Wrapper
      $hasMessage={
        !!noFuturesAccountMessageData?.show ||
        !!quickConnectAlreadyUsedMessageData?.show
      }
    >
      {noFuturesAccountMessageData?.displayMessage}
      {quickConnectAlreadyUsedMessageData?.displayMessage}
      <Button
        type="primary"
        $size="l"
        onClick={primaryButtonData.onClick}
        loading={primaryButtonData.loading || isLoadingRedirect}
        disabled={primaryButtonData.disabled}
        data-testid={QUICK_CONNECT_SUBMIT_BUTTON_ID}
        style={{
          margin: isMobile ? "0 0 16px 0" : "12px 0 16px 0",
          width: "100%",
        }}
      >
        {primaryButtonData.text}
      </Button>
      <SecondaryButton
        onClick={() => {
          trackAccountSetup("Connect Account - Method Changed", {
            ...connectProperties,
            connect_method: "api_keys",
          });
          secondaryButtonData.onClick();
        }}
      >
        {secondaryButtonData.text}
      </SecondaryButton>
      {isMobile && !user?.is_onboarding && (
        <ConnectExchangeGuide
          primaryExchange={primaryExchange}
          isQuickConnect={true}
        />
      )}
      {user?.is_onboarding && (
        <WhitelistIPsMessage primaryExchange={primaryExchange} />
      )}
      {quotaModal}
    </Wrapper>
  );
};

export default QuickConnectForm;

const Wrapper = styled.div<{ $hasMessage?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  /* A message right under the form keeps the field's own spacing. */
  margin-top: ${(p) => (p.$hasMessage ? 0 : 24)}px;
`;
